import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, Endpoint, Route, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HPMSOnClickTermUse } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FormAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnClose, OnMount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NewCallAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { Call } from './calls-controller';

interface CallType {
  dsCallType: string,
  nrSequenceCallType: number,
  required: string,
  nrSeqResponsabilityTerm: number
}

@Controller({ fragment: FragmentNames.NEW_CALL })
export default class NewCalledController extends BaseController {

  @Inject(Aliases.FORM_NEW_CALL)
  private formNewCall: FormAPI;
  @Inject(Aliases.FIELD_REQUIRED_ATTACHMENT)
  private fieldRequiredAttachment: FieldAPI;
  @Inject(Aliases.FETCH_GUIDANCE)
  private fetchGuidance: FetchAPI;
  @Inject(Aliases.TEXT_GUIDANCE)
  private textGuidance: TextAPI;
  @Inject(Aliases.FIELD_RESPONSABILITY_TERM)
  private fieldResponsabilityTerm: FieldAPI;
  @Inject(Aliases.PANEL_RESPONSABILITY_TERM)
  private panelResponsabilityTerm: PanelAPI;
  @Inject(Aliases.FETCH_TERM)
  private fetchTerm: FetchAPI;

  @OnMount(Aliases.PANEL_NEW_CALL)
  onMountPanel(): void {
    const { nrSeqUser, nrSeqCategory } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser, nrSeqCategory });
  }

  @OnChange(Aliases.AUTOCOMPLETE_TYPE)
  onChangeCallType(event: ChangeEvent<CallType>): void {
    const { nrSeqCategory } = this.historyService.getCurrentRouteParams();
    const { required, nrSequenceCallType, nrSeqResponsabilityTerm } = event.value;
    if (nrSeqResponsabilityTerm) {
      this.context.set({ nrSeqResponsabilityTerm });
      this.fieldResponsabilityTerm.setVisible(true);
      this.fieldResponsabilityTerm.setValidators([{ name: 'required', options: {} }] as Validator[]);
    } else {
      this.context.set({ nrSeqResponsabilityTerm });
      this.fieldResponsabilityTerm.setVisible(false);
      this.fieldResponsabilityTerm.setValidators([{}] as Validator[]);
    }
    this.fetchGuidance.setParameters({ nrSeqCategory, nrSequenceCallType });
    this.fetchGuidance.forceFetch();
    if (required && required === 'S') {
      this.fieldRequiredAttachment.setVisible(true);
    } else {
      this.fieldRequiredAttachment.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_GUIDANCE)
  onAfterFetchGuidance(event: FetchEvent): void {
    const data = event.data as Call;
    if (isEmptyArrayOrObject(data.guidance)) {
      this.textGuidance.setValue('');
    }
  }

  @OnBeforeSubmit(Aliases.FORM_NEW_CALL)
  OnBeforeSubmit(event: SubmitEvent): void {
    const record = event.record;
    const { nrSeqAccreditation } = record.accreditation;
    const { nrSeqUser, nrSeqCategory } = this.historyService.getCurrentRouteParams();
    const callCategory = { nrSeqCategory };
    const body = { ...record, callCategory, nrSeqAccreditation };
    this.formNewCall.setLoading(true);
    HttpService.post<Call>(Endpoint.POST.NEW_CALL, { nrSeqUser }, { body })
      .then((response) => {
        if (!response.body) return;
        const { nrSeqCall } = response.body;
        this.historyService.push(Route.DETAILS_CALLED, { nrSeqCall, nrSeqUser });
      })
      .catch((response) => {
        this.formNewCall.setLoading(false);
        this.formNewCall.setEditing(true);
        ToastServiceProviderAccreditation.error(response.body);
      });
  }

  @OnCancel(Aliases.FORM_NEW_CALL)
  oncancel(): void {
    this.historyService.push(APPLICATION_ROUTES.CALLS);
  }

  @HPMSOnClickTermUse(Aliases.HPMS_TERM_RESPONSABILITY_CHECKBOX)
  onClickResponsabilityTerm(): void {
    const { nrSeqResponsabilityTerm } = this.context.getBulkData();
    this.panelResponsabilityTerm.setVisible(true);
    this.fetchTerm.setParameters({ nrSeqResponsabilityTerm });
    this.fetchTerm.forceFetch();
  }

  @OnClose(Aliases.MODAL_RESPONSABILITY_TERM)
  onCloseModalResponsabilityTerm(): void {
    this.panelResponsabilityTerm.setVisible(false);
  }
}
